import { Link } from '@mui/material'
import { isEqual, isObject, sortBy, trim } from 'lodash'
import { toast } from 'react-toastify'

import { NEW_LINE } from '~/constants'
import { EWebScrapeStatus } from '~/features/agents/models'
import {
  EConvoOptionsKeys,
  NOTIFICATION_TYPE_NONE,
  TCustomInstruction,
  TNotificationTypeFormValue,
} from '~/features/settings/models'
import { EServerPath } from '~/models/api'

const queryKeys = {
  emailAddressAssignment: (filters: object = {}) => [
    EServerPath.emailAddressAssignments,
    filters,
  ],
  recordIntroBlendAudio: (agentId: string) => [
    EServerPath.callsIntroBlendAudio,
    agentId,
  ],
  analysisFields: (orgId: string, filters?: object) =>
    filters === undefined
      ? [EServerPath.analysisFields, orgId]
      : [EServerPath.analysisFields, orgId, filters],
  analysisFieldsAssistantRules: (
    orgId: string,
    agentId?: string,
    status?: string,
  ) =>
    agentId === undefined
      ? [EServerPath.analysisFieldsAssistantRules, orgId]
      : [EServerPath.analysisFieldsAssistantRules, orgId, agentId, status],
  analysisField: (orgId: string, fieldId: string) => [
    EServerPath.analysisField,
    orgId,
    fieldId,
  ],
}

function compareArrayOfObjects<T extends object>(
  arr1: T[],
  arr2: T[],
): T[] | null {
  if (!isEqual(sortBy(arr1), sortBy(arr2))) {
    return arr2
  }
  return null
}

export const getChangedValues = <T extends object>(
  values: T,
  initialValues: T,
): Partial<T> => {
  const changedValues: Partial<Record<keyof T, unknown>> = {}

  Object.keys(values).forEach((key) => {
    const value = values[key as keyof T]

    const initialValue = initialValues[key as keyof T]

    if (value !== initialValue) {
      // todo: parsing should not be done here, but in the form component.
      if (
        key === EConvoOptionsKeys.questions ||
        key === EConvoOptionsKeys.facts
      ) {
        const stringValue = value as unknown as string
        changedValues[key as keyof T] = stringValue
          .split(NEW_LINE)
          .map(trim)
          .filter(Boolean)
      } else if (
        key === EConvoOptionsKeys.customInstructions &&
        Array.isArray(value)
      ) {
        const changedCustomInstructions = compareArrayOfObjects(
          initialValue as TCustomInstruction[],
          value as TCustomInstruction[],
        )
        if (changedCustomInstructions !== null) {
          changedValues[key as keyof T] = changedCustomInstructions
        }
      } else if (
        !Array.isArray(value) &&
        isObject(value) &&
        isObject(initialValue)
      ) {
        const nestedChangedValues = getChangedValues(value, initialValue)

        if (Object.keys(nestedChangedValues).length > 0) {
          changedValues[key as keyof T] = nestedChangedValues
        }
      } else {
        changedValues[key as keyof T] = value
      }
    }
  })

  return changedValues as Partial<T>
}

export const getWebsiteAnalysisContent = (
  scrapeStatus: EWebScrapeStatus,
  supportEmail: string,
) => {
  switch (scrapeStatus) {
    case EWebScrapeStatus.failed:
      return {
        title: 'Website Analysis Unsuccessful',
        content: (
          <>
            There was an issue in analyzing your website. Send us a note at{' '}
            <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link> if you
            would like your website details included.
          </>
        ),
      }
    default:
      return {
        title: 'Website Analysis Complete',
        content: (
          <>
            We have indexed the details from your website and trained your AI
            Receptionist on it.
          </>
        ),
      }
  }
}

export const webScrapingLoadingTexts = [
  'Initiating website analysis: Collecting data from all relevant pages.',
  'Evaluating website structure and navigation for optimal user experience.',
  'Analyzing content quality and keyword usage.',
  'Assessing website performance metrics, including load time and responsiveness.',
  'Finalizing analysis: Compiling results and generating comprehensive report.',
]

export const showDuplicatedItemToast = (textToBeAdded: string) => {
  const slice = textToBeAdded.slice(0, 30)
  const isSliceSmallerThanWhole = slice.length < textToBeAdded.length
  toast.error(
    `The fact "${slice}${isSliceSmallerThanWhole ? '...' : ''}" already exists`,
  )
}

export const parseSettingsRules = (rules: string) =>
  rules
    .split(NEW_LINE)
    .map((rule) => rule.trim())
    .filter(Boolean)

export const isNotificationTypeNone = (
  notifyType: TNotificationTypeFormValue,
): notifyType is typeof NOTIFICATION_TYPE_NONE =>
  notifyType === NOTIFICATION_TYPE_NONE

export default queryKeys
